import { reactive } from 'vue';
import { taxPlaygroundV2Api } from '../services/taxPlaygroundV2Api';

/**
 * Reactive state for the Tax Playground V2 console (S3-P4). The live API
 * (`services/taxPlaygroundV2Api.js`) is the source of truth; this module is a
 * plain reactive singleton — NOT Vuex, NOT Pinia (same house style as
 * `taxConfigV2Store.js` / `taxQuestionV2Store.js`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE RULES THIS FILE EXISTS TO ENFORCE — do not "improve" them away:
 *
 * 1. 🔴 `counts` IS RENDERED VERBATIM. The store never re-tallies, never fills
 *    in missing keys, never defaults to 0, never drops zero-valued keys. What
 *    the server sent is what the screen shows, even when it disagrees with the
 *    rows (TC-S3P4-63 proves this by tampering with the response). The only
 *    number the FE may compute is `askableCount` for the ">80" banner, and that
 *    belongs to the view, not here.
 *
 * 2. 🔴 `counts` KEYS ARE snake_case ON THE WIRE: `answered`, `pending`,
 *    `not_started`, `partial_answer`, `skipped`, `not_applicable`. They are
 *    pinned by `[JsonPropertyName]` on `ConfigFormV2CountsDto`
 *    (`Model/AppModel/TaxPlaygroundV2Models.cs:385-393`). Read them as
 *    `counts['not_started']` — `counts.notStarted` is always `undefined`.
 *    Everything ELSE in this slice is plain camelCase.
 *
 * 3. 🔴 `state.turns` IS SORTED ASC BY `turnId` (oldest → newest). NOT by
 *    `turnNo`: the backend allocates it with `MAX(TurnNo)+1` outside any
 *    transaction and there is no unique index, so two browser tabs on the same
 *    session legitimately produce duplicate `turnNo` values. `turnId` is
 *    `IDENTITY(1,1)` and is the only trustworthy ordering. The API returns
 *    newest → oldest (`ORDER BY TurnNo DESC`, spec line 417) and `loadTurns()`
 *    flips it. A view that wants newest-first must reverse a COPY itself —
 *    `isRepeatTurn(i)` compares against `turns[i-1]` and breaks under DESC.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Id casing: every id ends in `…Id` (`turnId`, `formId`, `yearId`,
 * `questionItemId`) because the DTOs declare `TurnId`/`FormId`. Do NOT copy the
 * `yearID`/`versionID` spelling from `taxConfigV2Store.js` — those are genuinely
 * different DTOs, and `turnID` here just reads `undefined`.
 *
 * Truncation: the BAL size guard (256 KB per column) can replace any big JSON
 * blob with the sentinel `{"__truncated":true,"reason":"…","bytes":N}`. The
 * store passes it through untouched (`isTruncated()` lets the view raise the
 * L14 warning) — "repairing" it to `{}` would hide real data loss.
 *
 * @typedef {Object} QuickReply
 *   🔴 `label` and nothing else. A chip TYPES its own text: pressing it sends `label` as the next message.
 *   Do not reintroduce a `value` — a `{label,value}` pair is what made pressing "ชาย" send "male".
 *   (Turns persisted before 2026-07-27 may still carry a stray `value` key; ignore it, `label` is authoritative.)
 * @property {?string} label
 *
 * @typedef {Object} ConfigFormCounts
 *   🔴 snake_case keys, verbatim from the server. Any key may be absent.
 * @property {number} answered @property {number} pending @property {number} not_started
 * @property {number} partial_answer @property {number} skipped @property {number} not_applicable
 *
 * @typedef {Object} ConfigFormField
 * @property {string} fieldKey  ("item_{questionItemId}" when the item has no fieldKey)
 * @property {number} questionItemId @property {?string} label @property {string} group
 * @property {?string} kind
 * @property {'answered'|'pending'|'not_started'|'partial_answer'|'skipped'|'not_applicable'} status
 * @property {?string} value @property {?string} customerInput  (shown only when status === 'answered')
 * @property {?string} reason @property {number} sortOrder @property {boolean} isRequired
 * @property {?string} visibleWhen @property {?boolean} isComplete
 *
 * @typedef {Object} ConfigFormGroup
 * @property {string} group @property {string} groupLabel @property {number} sortOrder
 * @property {ConfigFormField[]} fields
 *
 * @typedef {Object} ConfigForm
 * @property {ConfigFormCounts} counts
 * @property {ConfigFormGroup[]} groups   ARRAY — not a dictionary (unlike `variables.taxPlanningV2ConfigForm`)
 *
 * @typedef {Object} PromptLayer
 * @property {?string} marker  the REAL `### … ###` marker line of that section (`(base system prompt)` for the unmarked base)
 * @property {?string} text
 * @property {?string} source  `node:<nodeDataKey>` (flow node inspector) | `config:TaxQuestionStepV2.<Col>` | `config:TaxQuestionItemV2.<Col>` | `config:TaxPlanningSettingsV2.<Col>` (global SETTINGS dialog) | `nodeDefault` (built-in default, i.e. NOT overridden yet)
 * @property {?string} sourceGroupCode @property {?string} sourceLabelTh  Thai provenance copy owned by the backend — always set
 * @property {?number} sourceQuestionItemId  QuestionItemID whose column this layer's text came from — set ONLY on the
 *   item-level layers (`### Tax Question Prompt ###`, and `### Question Context ###` on the ask stack). 🔴 On
 *   `extractFields` it is the FOCUS item (the question being answered), NOT `turn.asked` (the question asked next).
 *   Null on turns captured before the field existed. Provenance only — the FE reads `sourceLabelTh` / `editRef`.
 * @property {?string} sourceItemLabel  label of `sourceQuestionItemId` as the node knew it (backend-side fallback)
 * @property {?Object} editRef  { kind, configVersionId, questionVersionId, stepId, groupCode, questionItemId, groupLabel, itemLabel }
 *   `kind` = questionStep | questionItem | configVersion (= /TaxConfigurationV2 → SETTINGS). Null ONLY when no screen
 *   backs the layer: a `node:*` layer (flow canvas, not deep-linkable) or a `nodeDefault` layer with no settings
 *   column. 🔴 A `nodeDefault` layer CAN carry a ref — never key "editable" off `source`, key it off `editRef`.
 *
 * @typedef {Object} LlmCall
 * @property {?string} op  extractFields | phraseQuestion
 * @property {?string} opLabelTh  Thai label owned by the backend
 * @property {?string} model @property {?string} systemPrompt @property {PromptLayer[]} promptLayers  both ops carry layers
 * @property {?string} userText  for `phraseQuestion`/`editRequestAsk` = the fixed task line `Write Genie's next message in {Language} only[. End with the question.]` (since 2026-10-05; EMPTY on turns persisted before that — the question still rides in the `### Question Context ###` layer of the system prompt, so blank there is not an anomaly) · for `extractFields` = the customer's message
 * @property {?string} rawResponse @property {boolean} jsonMode
 * @property {number} promptTokens @property {number} completionTokens @property {number} totalTokens
 * @property {number} durationMs @property {number} statusReturn @property {?string} error
 *
 * @typedef {Object} TurnDto
 *   Payload of BOTH `POST /chat` and `GET /turns/{turnId}`.
 * @property {number} turnId @property {number} turnNo @property {string} sessionId
 * @property {number} formId @property {string} userId @property {?string} userMessage @property {?string} reply
 * @property {QuickReply[]} quickReply @property {?string} quickReplySource
 * @property {?string} taxCards  every `|taxcard|({…})` payload on the turn, collected into ONE JSON array and
 *   handed over as a STRING — the wire carries one OBJECT per marker; the array is ours, so a turn with two cards
 *   is still one value. Lifted out of `reply` — null
 *   on a turn with no card. `reply` still carries the marker in-band; this is a convenience for code that wants
 *   the payload alone, never a second source. 🔴 The CARDS ON SCREEN are NOT built from this field: the view
 *   splits `reply` itself (`splitTaxCards`) so a payload that fails to parse is still visible as debris. This
 *   field is read as the SECOND opinion — its length is compared against the FE split to raise `cardsDiffer`.
 *   The bubble itself shows the marker-free copy by default, with a "ดู marker ดิบ" switch to put the raw wire
 *   back for a by-eye format check.
 * @property {?Object} asked  { questionItemId, fieldKey, groupCode, itemKind, questionTextTh, label, inputType, answerType, visibleWhen, isRequired, sortOrder, resolvedBy } — null when the form is Completed
 * @property {LlmCall[]} llmCalls  length varies: first turn 1 · normal 2 · re-ask 1 · form-complete 1 · year-select 0
 * @property {?string} trace @property {?ConfigForm} configForm @property {string[]} changedPaths
 * @property {?Object} taxState  { form, answers[] }
 * @property {Object} versions  { yearId, configVersionId, questionVersionId, overridden, versionSwitchAbandonedFormId }
 * @property {Object} progress  { answeredCount, totalRequired, currentGroupCode, formStatus }
 * @property {?Object} variables  the node's 9 `taxPlanningV2*` keys — for comparison only
 * @property {string} status  Success|Error — the node is fail-soft, so a failed LLM call still reports "Success"; detect it via `llmCalls[].statusReturn != 0` (binding; `error` is supplementary and can be `null` even when the call failed)
 * @property {?string} errorMessage @property {number} durationMs @property {string} createdDate
 *
 * @typedef {Object} TurnSummary
 *   Timeline row of `GET /turns` (light columns only).
 * @property {number} turnId @property {number} turnNo @property {number} formId
 * @property {?string} userMessage @property {?string} replyPreview
 * @property {?string} taxCards  SAME field, same meaning, same wire shape as `TurnDto.taxCards` above — the
 *   JSON ARRAY STRING of this turn's `|taxcard|` payloads. Carried on the light row on purpose: `replyPreview`
 *   is cut at 160 chars AND already marker-free, so without it a turn that is not the selected one has NO way
 *   to show its card. 🔴 Read it with `parseTaxCardsField`, never `JSON.parse` — and never assume it is there:
 *   a backend older than this field simply omits it (`undefined` ⇒ `[]` ⇒ the pre-existing "cards only on the
 *   active turn" behaviour), which is what keeps the FE deployable ahead of the API.
 * @property {?string} askedFieldKey @property {?string} askedGroupCode
 * @property {?ConfigFormCounts} counts @property {number} changedCount @property {number} llmCallCount
 * @property {number} totalTokens @property {string} status @property {number} durationMs @property {string} createdDate
 *
 * @typedef {Object} PlaygroundOptions
 * @property {Object[]} years  { yearId, taxYearCode, beYear, year, isActive, configVersions[] { versionId, versionCode, isActive, questionVersions[] { questionVersionId, versionCode, isActive } } }
 * @property {Object[]} settingsVersions  { settingsVersionId, versionCode, description, isActive } — every
 *   `tax.TaxPlanningSettingsV2` version; EMPTY when the settings table has not been applied yet (fail-soft).
 * @property {Object[]} models  ⚠️ DEPRECATED — { modelName, provider, maxToken }. The playground no longer
 *   overrides the model per turn (the model lives in the resolved settings version). Kept because the backend
 *   still returns the field; nothing reads it.
 * @property {Object} defaults  { yearId, configVersionId, questionVersionId, settingsVersionId, modelName }
 *   — the active chain (0 = none active). `modelName` is DEPRECATED, see above.
 *
 * @typedef {Object} StateDto
 *   Payload of `GET /state` (peek — no LLM call, no write).
 * @property {boolean} hasForm @property {number} formId @property {?Object} taxState
 * @property {?ConfigForm} configForm @property {?Object} nextQuestion @property {QuickReply[]} quickReply
 * @property {?string} quickReplySource
 *   `answerOptions` | `confirm` | `incomeCategories` | `yearSelect` | `optedOut` | `none` | null, plus the
 *   peek-only sources of the state kinds below: `startConfirm` / `resume` (the chips that belong to the state)
 *   and `startConfirmMarker` / `resumeMarker` (the author put a `|quickReply|` marker in the prompt and the BAL
 *   parsed it, so those are the AUTHOR's chips). `SELECT_YEAR` always stays `yearSelect` — those chips are what
 *   `MatchYear` parses back.
 * @property {string} kind
 *   `START_CONFIRM` (no form yet — greeting) | `SELECT_YEAR` (must pick a tax year) | `RESUME` (a form is still
 *   InProgress) | `QUESTION` (walk the questions as before). 🔴 The DTO DEFAULTS to `QUESTION`
 *   (`TaxPlaygroundStateDto.Kind`), so a payload from an older backend reads as the pre-existing behaviour.
 * @property {?string} message
 *   Thai status text belonging to `kind`, deterministic (template + token substitution — never an LLM call).
 *   `null` when `kind === 'QUESTION'` — that is the "no status text" case, NOT an empty bubble to render.
 *   🔴 MAY CONTAIN `\n`: `SELECT_YEAR` appends the year list (`\n- ปีภาษี {BeYear}`) to the lead sentence, so
 *   whatever renders it needs `white-space: pre-wrap` or the list collapses onto one line.
 * @property {Object} progress @property {Object} versions
 * @property {number} lastTurnId
 */

/**
 * localStorage key holding the persisted preferences: the 6 selector prefs
 * (session / user / the version triple / settings version) plus the 2 layout
 * prefs (`timelineCollapsed` / `inspectorFull`). Layout lives here — and not in
 * the view's display-only `data()` — because the admin asked for the column
 * split to survive a refresh; everything else about the layout is still the
 * view's business.
 */
export const PREFS_KEY = 'taxPlaygroundV2.prefs';

/**
 * Fixed display order of the 6 counts chips (§UX ruling A / TC-S3P4-63).
 *
 * 🔴 The constant itself now LIVES in `utils/taxPlaygroundV2Counts.js` — a
 * dependency-free module — because `TaxPlaygroundConfigFormPanelV2.vue` is also
 * mounted by the READ-ONLY Tax Log V2 viewer, and importing it from here would
 * drag this store (and with it `services/taxPlaygroundV2Api`'s POST/PUT calls
 * plus a module-level `reactive` singleton) into that page's bundle. It is
 * re-exported here so the store's public surface is unchanged.
 */
export { COUNTS_ORDER } from './taxPlaygroundV2Counts';

/** Playground user ids must start with `PG-` so test data can never be mistaken for a real CIF. */
const DEFAULT_USER_ID = 'PG-';

const state = reactive({
  /** @type {PlaygroundOptions} never null — the view checks `options.years.length` directly */
  options: {
    years: [],
    settingsVersions: [],
    /** ⚠️ DEPRECATED — mirrored from the backend for back-compat; no code reads it. */
    models: [],
    defaults: { yearId: 0, configVersionId: 0, questionVersionId: 0, settingsVersionId: 0, modelName: null },
  },
  optionsLoading: false,
  /** true once loadOptions() has settled (success OR failure) — separates "still loading" (L1) from "loaded, no active year" (L3) */
  optionsLoaded: false,

  sessionId: '',
  userId: DEFAULT_USER_ID,

  /** version override triple — all three or none (see overrideTriple()) */
  yearId: null,
  configVersionId: null,
  questionVersionId: null,
  /**
   * Which `tax.TaxPlanningSettingsV2` VERSION the turn runs on — it REPLACED the old `modelName`
   * override (D5): provider/model/temperature/prompts all live in that version now, so the console
   * and the flow node can no longer drift apart. `null` ⇒ the backend resolves the ACTIVE version.
   * 🔴 It must be sent on BOTH `/chat` and `/state`, otherwise the Get Tax State preview shows the
   * greeting/resume/year copy of a DIFFERENT version from the one the next turn will actually run.
   */
  settingsVersionId: null,

  /** @type {TurnSummary[]} 🔴 ASC by turnId (oldest → newest) */
  turns: [],
  /** total rows the server reports for the session (may exceed turns.length when paged) */
  turnsTotal: 0,
  /** @type {?TurnDto} the turn the Inspector is showing */
  activeTurn: null,
  /**
   * @type {?StateDto} last `Get Tax State` peek — a different shape from activeTurn, so it needs its own slot.
   * 🔴 It is a SIBLING of `activeTurn`, never a substitute: a peek must never overwrite the turn the Inspector
   * is showing (D1). It carries `kind` / `message` (the status bubble) on top of the form data.
   */
  lastState: null,

  /**
   * Layout prefs (persisted). Both DEFAULT TO FALSE, which is the pre-existing
   * 3-column layout — a prefs blob written before these keys existed simply has
   * no opinion and degrades to that default (see loadPrefs()).
   *  · `timelineCollapsed` — column A shrinks to a rail and hands its width to the inspector.
   *  · `inspectorFull`     — columns A+B are dropped and the inspector takes the whole row.
   * They are independent: leaving `inspectorFull` restores whatever A was doing.
   */
  timelineCollapsed: false,
  inspectorFull: false,

  sending: false,
  loadingTurn: false,
  loadingTurns: false,
  resetting: false,
  peeking: false,
  /** export or import in flight — one flag, they are never concurrent */
  snapshotBusy: false,

  /** @type {?string} Thai error text for the snackbar */
  error: null,
  /** 🔴 L15: the deep-linked turn is gone (or belongs to another company) — an EMPTY STATE, not an error */
  turnNotFound: false,
});

// ── errors ───────────────────────────────────────────────────────────────
function setError(err) {
  state.error = (err && (err.message || String(err))) || 'เกิดข้อผิดพลาด';
}

function clearError() {
  state.error = null;
}

// ── small pure helpers ───────────────────────────────────────────────────
/** Coerce a selector value to a positive int id, or null. */
function toIdOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * `PG-` guard, case-insensitive (`pg-cif001` passes). Purely a courtesy check to
 * avoid a guaranteed 400 — the BAL validates again. 🔴 It never NORMALIZES:
 * a user who typed `CIF001` keeps `CIF001` and gets told off (L8 / TC-S3P4-57).
 */
function isPgUserId(value) {
  return /^pg-/i.test(String(value || '').trim());
}

/**
 * True when a JSON blob was replaced by the BAL size-guard sentinel
 * `{"__truncated":true,"reason":"…","bytes":N}`. The three key names are a
 * contract with the backend — do not rename them.
 */
function isTruncated(value) {
  return Boolean(value && typeof value === 'object' && value.__truncated === true);
}

/** The forced triple, or all-null. Sending a partial triple makes the node resolve the active chain (spec line 251). */
function overrideTriple() {
  const yearId = toIdOrNull(state.yearId);
  const configVersionId = toIdOrNull(state.configVersionId);
  const questionVersionId = toIdOrNull(state.questionVersionId);
  if (yearId && configVersionId && questionVersionId) {
    return { yearId, configVersionId, questionVersionId };
  }
  return { yearId: null, configVersionId: null, questionVersionId: null };
}

/** Request body of `POST /chat`. */
function buildChatBody(text, formSubmit) {
  const o = overrideTriple();
  return {
    sessionId: state.sessionId,
    userId: state.userId,
    message: text,
    // 🔴 Omitted entirely when absent, never sent as null: `formSubmit` is the card
    // postback path and the node branches on "is there one at all". An always-present
    // key would make every ordinary chat turn look like a press of a card nobody drew.
    ...(formSubmit ? { formSubmit } : {}),
    yearId: o.yearId,
    configVersionId: o.configVersionId,
    questionVersionId: o.questionVersionId,
    // 🔴 null (not 0) when nothing is pinned — the backend reads null/0 alike as
    // "run the ACTIVE version", but null is the shape the DTO documents.
    settingsVersionId: toIdOrNull(state.settingsVersionId),
    // 🔴 The bubble the tab has ALREADY DRAWN, echoed back exactly the way the real
    // app echoes it — this is what made the playground take a different code path
    // from mobile for the same sentence.
    //
    // The app calls the state read on entry, renders the resume bubble it returns,
    // and forwards that very text as `continueQuestion` on the NEXT send (once —
    // the turn after that carries null). The node reads it in
    // `ResumeGateAlreadyAsked`: with it, the «ทำต่อ / สร้างแผนใหม่» screen counts
    // as already asked and the gate DECIDES on the first tap; without it `asked`
    // stays false and the turn falls back to "before the ask, NEW only", which is
    // a different branch, a different log, and a reproduction that cannot fail the
    // way the customer's did.
    //
    // Sending it once falls out for free: `send()` clears `lastState` after the
    // POST resolves, so the following turn has nothing to echo — the same
    // once-only shape the app has.
    //
    // 🔴 Omitted (not null) when there is no peek to echo, so a tab that never
    // pressed Get Tax State posts exactly the body it posted before.
    ...(state.lastState && state.lastState.message
      ? { continueQuestion: state.lastState.message }
      : {}),
    channel: 'playground',
    persistTurn: true,
  };
}

// ── preferences (localStorage — best effort, never fatal) ─────────────────
/**
 * Read the persisted prefs. Private-browsing mode, a full quota or corrupt
 * JSON must never take the page down — the defaults stay usable.
 *
 * 🔴 MIGRATION (settings-versioning): the blob written by the model-picker era
 * carries `modelName` and NO `settingsVersionId`. It is read forward safely and
 * on purpose: the dead `modelName` key is simply never looked at, and a missing
 * `settingsVersionId` reads as `null` ⇒ `applyDefaultsToEmptySlots()` fills in
 * the ACTIVE version from `/options`. The first `savePrefs()` (loadOptions()
 * always calls one) rewrites the whole blob, so the stale key disappears by
 * itself — no key bump, no clean-up pass, and no old preference can pin a
 * settings version the admin never chose.
 */
function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return;
    const p = JSON.parse(raw);
    if (!p || typeof p !== 'object') return;
    if (typeof p.sessionId === 'string') state.sessionId = p.sessionId;
    state.userId = typeof p.userId === 'string' && p.userId.trim() !== '' ? p.userId : DEFAULT_USER_ID;
    state.yearId = toIdOrNull(p.yearId);
    state.configVersionId = toIdOrNull(p.configVersionId);
    state.questionVersionId = toIdOrNull(p.questionVersionId);
    state.settingsVersionId = toIdOrNull(p.settingsVersionId);
    // 🔴 `=== true`, not `Boolean(p.x)` and not `p.x ?? false`: an OLD blob has no
    // such key, a corrupt one may hold a string — both must read as the default
    // 3-column layout rather than as "collapsed".
    state.timelineCollapsed = p.timelineCollapsed === true;
    state.inspectorFull = p.inspectorFull === true;
  } catch (e) {
    // unreadable prefs are not an error worth surfacing — fall back to defaults
  }
}

/** Persist ONLY the 6 selector prefs + the 2 layout prefs. Never turns/activeTurn/error. */
function savePrefs() {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({
      sessionId: state.sessionId,
      userId: state.userId,
      yearId: state.yearId,
      configVersionId: state.configVersionId,
      questionVersionId: state.questionVersionId,
      settingsVersionId: state.settingsVersionId,
      timelineCollapsed: state.timelineCollapsed,
      inspectorFull: state.inspectorFull,
    }));
  } catch (e) {
    // quota / private mode — preferences are a convenience, not state we depend on
  }
}

function setSessionId(v) {
  state.sessionId = String(v ?? '');
  savePrefs();
}

/** 🔴 Stores the raw text. No `PG-` prefixing behind the user's back. */
function setUserId(v) {
  state.userId = String(v ?? '');
  savePrefs();
}

function setVersionSelection({ yearId, configVersionId, questionVersionId } = {}) {
  state.yearId = toIdOrNull(yearId);
  state.configVersionId = toIdOrNull(configVersionId);
  state.questionVersionId = toIdOrNull(questionVersionId);
  savePrefs();
}

/**
 * Pick the settings version the next turn runs on. `null` / 0 / a cleared select
 * all mean "use the ACTIVE version" — the console never pins a version it did
 * not receive from `/options`.
 * 🔴 Unlike the year/config/question triple this does NOT abandon the current
 * form: the settings version is not part of the form's identity (only the LLM
 * config + prompt copy of the NEXT turn), so no confirm dialog belongs here.
 */
function setSettingsVersionId(v) {
  state.settingsVersionId = toIdOrNull(v);
  savePrefs();
}

/**
 * Layout only — neither of these touches a request body, a version selection or
 * the form. They are setters (not `v-model` targets) for the same reason the
 * version selectors are: the view stays the only place that decides WHEN a
 * layout change is committed.
 */
function setTimelineCollapsed(v) {
  state.timelineCollapsed = Boolean(v);
  savePrefs();
}

function setInspectorFull(v) {
  state.inspectorFull = Boolean(v);
  savePrefs();
}

// ── getters ──────────────────────────────────────────────────────────────
/**
 * B6 "⟳ ถามซ้ำ": turn `idx` asked the SAME question as the turn before it and
 * changed nothing. 🔴 `Boolean(k)` matters — two turns with a null
 * `askedFieldKey` (year select, completed form) are not "the same question
 * twice", they asked nothing at all.
 * @param {number} idx index into `state.turns` (which is ASC by turnId)
 */
function isRepeatTurn(idx) {
  const i = Number(idx);
  if (!(i > 0) || i >= state.turns.length) return false;
  const cur = state.turns[i];
  const prev = state.turns[i - 1];
  if (!cur || !prev) return false;
  const k = cur.askedFieldKey;
  return Boolean(k) && k === prev.askedFieldKey && (cur.changedCount ?? 0) === 0;
}

/**
 * How many trailing turns are flagged as repeats (§UX ruling C alert).
 * A streak of 2 means the same question was asked on 3 consecutive turns.
 */
function repeatStreak() {
  let n = 0;
  for (let i = state.turns.length - 1; i > 0; i -= 1) {
    if (!isRepeatTurn(i)) break;
    n += 1;
  }
  return n;
}

// ── actions ──────────────────────────────────────────────────────────────
/** Fill any selector the user has NOT chosen yet with the active chain. Never overwrites a choice. */
function applyDefaultsToEmptySlots() {
  const d = state.options.defaults || {};
  if (state.yearId == null && (d.yearId ?? 0) > 0) state.yearId = d.yearId;
  if (state.configVersionId == null && (d.configVersionId ?? 0) > 0) state.configVersionId = d.configVersionId;
  if (state.questionVersionId == null && (d.questionVersionId ?? 0) > 0) state.questionVersionId = d.questionVersionId;
  if (state.settingsVersionId == null && (d.settingsVersionId ?? 0) > 0) state.settingsVersionId = d.settingsVersionId;
}

/**
 * (1) Bootstrap the selectors: years/versions incl. drafts + settings versions + defaults.
 * @returns {Promise<PlaygroundOptions>}
 */
async function loadOptions() {
  state.optionsLoading = true;
  clearError();
  try {
    const dto = await taxPlaygroundV2Api.getOptions();
    state.options = {
      years: Array.isArray(dto && dto.years) ? dto.years : [],
      settingsVersions: Array.isArray(dto && dto.settingsVersions) ? dto.settingsVersions : [],
      models: Array.isArray(dto && dto.models) ? dto.models : [],
      defaults: {
        yearId: (dto && dto.defaults && dto.defaults.yearId) ?? 0,
        configVersionId: (dto && dto.defaults && dto.defaults.configVersionId) ?? 0,
        questionVersionId: (dto && dto.defaults && dto.defaults.questionVersionId) ?? 0,
        settingsVersionId: (dto && dto.defaults && dto.defaults.settingsVersionId) ?? 0,
        modelName: (dto && dto.defaults && dto.defaults.modelName) || null,
      },
    };
    // A settings version that was deleted (or never existed on this backend) must not
    // stay pinned from localStorage — the list is the authority. Dropping it here lets
    // applyDefaultsToEmptySlots() re-select the ACTIVE version instead of silently
    // sending a dead id that the backend would fall back on anyway (A10).
    if (state.settingsVersionId != null
      && !state.options.settingsVersions.some((v) => v && v.settingsVersionId === state.settingsVersionId)) {
      state.settingsVersionId = null;
    }
    applyDefaultsToEmptySlots();
    savePrefs();
    return state.options;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.optionsLoading = false;
    state.optionsLoaded = true;
  }
}

/**
 * (5) Reload the session's timeline. 🔴 Re-sorted ASC by `turnId` — see rule 3.
 * @returns {Promise<TurnSummary[]>}
 */
async function loadTurns(page = 1, pageSize = 50) {
  if (!state.sessionId) {
    state.turns = [];
    state.turnsTotal = 0;
    return state.turns;
  }
  state.loadingTurns = true;
  try {
    const res = await taxPlaygroundV2Api.listTurns(state.sessionId, page, pageSize);
    const items = Array.isArray(res && res.items) ? res.items : [];
    state.turns = items.slice().sort((a, b) => ((a && a.turnId) ?? 0) - ((b && b.turnId) ?? 0));
    state.turnsTotal = (res && res.totalCount) ?? 0;
    return state.turns;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.loadingTurns = false;
  }
}

/**
 * (7) Start a fresh session id `pg-{yyyymmddhhmmss}-{rand4}` from LOCAL time and
 * drop everything tied to the old one. No API call — the form is created by the
 * next `/chat` turn.
 * @returns {string} the new sessionId
 */
function newSession() {
  const d = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}${p2(d.getHours())}${p2(d.getMinutes())}${p2(d.getSeconds())}`;
  const rand4 = Math.random().toString(36).slice(2, 6);
  state.sessionId = `pg-${stamp}-${rand4}`;
  state.turns = [];
  state.turnsTotal = 0;
  state.activeTurn = null;
  state.lastState = null;
  state.turnNotFound = false;
  state.error = null;
  savePrefs();
  return state.sessionId;
}

/**
 * (2) Send one turn. Empty text is a no-op; a missing session is created first;
 * a non-`PG-` userId throws WITHOUT hitting the API.
 *
 * 🔴 …unless a CARD POSTBACK comes with it. A press on a card carries no text — the
 * server writes the sentence the customer never typed from the payload it accepted —
 * so the empty-text guard would silently swallow every slider the mobile app sends.
 * That is the exact shape of turn this playground exists to prove.
 *
 * @param {string} message
 * @param {?Object} formSubmit `{ kind, fieldKey, action, values }` — the card postback.
 * @returns {Promise<?TurnDto>}
 */
async function send(message, formSubmit) {
  const text = String(message ?? '').trim();
  if (!text && !formSubmit) return null;
  if (!state.sessionId) newSession();
  clearError();
  if (!isPgUserId(state.userId)) {
    const guard = new Error('userId ของ Playground ต้องขึ้นต้นด้วย PG- (ห้ามใช้ CIF จริงของลูกค้า)');
    setError(guard);
    throw guard;
  }
  state.sending = true;
  try {
    const dto = await taxPlaygroundV2Api.sendChat(buildChatBody(text, formSubmit));
    state.activeTurn = dto;
    state.lastState = null;
    state.turnNotFound = false;
    // The timeline is a convenience view of the same data — a failed refresh
    // must never make a SUCCESSFUL turn look failed.
    try {
      await loadTurns();
    } catch (e) {
      // swallowed on purpose; loadTurns() already recorded state.error
    }
    return dto;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.sending = false;
  }
}

/**
 * (3) `Get Tax State` — peek without sending a message (no LLM call, no write).
 * Deliberately does NOT touch `activeTurn`: a peek is not a turn.
 *
 * 🔴 The whole DTO lands in `lastState` VERBATIM — including `kind` / `message`,
 * the two fields the view renders as the status bubble. Nothing is defaulted or
 * rewritten here: `kind` already defaults to `QUESTION` server-side, and
 * `message === null` is a real fact ("this state has no text"), not a hole to
 * fill with `''`. The Thai wording of `kind` is display copy and belongs to the
 * view (`TaxPlaygroundV2.vue` → `STATE_KIND_TH`).
 * @returns {Promise<?StateDto>}
 */
async function peekState() {
  if (!state.sessionId) return null;
  state.peeking = true;
  clearError();
  try {
    // 🔴 The SAME settings version `/chat` would use — the greeting / resume / year-select
    // copy of this peek comes out of that version, so omitting it here would preview one
    // version's wording while the next turn actually runs another's.
    // 🔴 …and the SAME version triple, via the same `overrideTriple()` `buildChatBody` uses (all
    // three or none). A finished plan whose config/question pair differs from the dropdown's (same
    // year) peeks as the version-choice screen, and the node's gate asks on that same condition —
    // this bubble is echoed back as `continueQuestion`, so a peek on another triple would draw a
    // screen the next turn does not agree it asked.
    const dto = await taxPlaygroundV2Api.getState(
      state.sessionId,
      state.userId || null,
      toIdOrNull(state.settingsVersionId),
      overrideTriple(),
    );
    state.lastState = dto;
    return dto;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.peeking = false;
  }
}

/**
 * (4) Abandon the session's current form (idempotent — `abandonedFormId = 0`
 * when there was nothing to abandon). The replacement form is created by the
 * next `/chat` turn, so `newFormId` is always 0 in this slice.
 * @param {{clearTurns?: boolean}} [opts]
 * @returns {Promise<Object>} { abandonedFormId, newFormId, clearedTurnCount }
 */
async function reset({ clearTurns } = {}) {
  const wantClearTurns = Boolean(clearTurns);
  state.resetting = true;
  clearError();
  try {
    const res = await taxPlaygroundV2Api.reset({
      sessionId: state.sessionId,
      userId: state.userId,
      yearId: state.yearId ?? null,
      configVersionId: state.configVersionId ?? null,
      questionVersionId: state.questionVersionId ?? null,
      clearTurns: wantClearTurns,
    });
    state.activeTurn = null;
    state.lastState = null;
    state.turnNotFound = false;
    if (wantClearTurns) {
      state.turns = [];
      state.turnsTotal = 0;
    }
    try {
      await loadTurns();
    } catch (e) {
      // swallowed on purpose; the reset itself succeeded
    }
    return res;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.resetting = false;
  }
}

/**
 * (5a) Snapshot this session's form. Pure read — nothing in the session changes,
 * so it is safe to click at any point in a conversation.
 * @returns {Promise<Object>} TaxPlaygroundFormSnapshotDto
 */
async function exportForm() {
  state.snapshotBusy = true;
  clearError();
  try {
    return await taxPlaygroundV2Api.exportForm(state.sessionId, state.userId || null);
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.snapshotBusy = false;
  }
}

/**
 * (5b) Fast-forward this session to a snapshot.
 *
 * 🔴 Everything the UI holds about the OLD form is dropped before we reload:
 * `lastState` / `activeTurn` describe a form that no longer exists, and leaving
 * them on screen next to the imported one is how a tester ends up reading turn 12
 * of the previous fixture as if it belonged to this one.
 *
 * The version triple is adopted from the snapshot so the toolbar shows the set
 * the imported form is actually answered against — importing a Q-version the
 * toolbar disagrees with would make the very next turn abandon the form it just
 * created (the /chat version-switch guard).
 *
 * @param {Object} snapshot parsed file
 * @param {{clearTurns?: boolean}} [opts]
 * @returns {Promise<Object>} import result (counts + warnings)
 */
async function importForm(snapshot, { clearTurns = true } = {}) {
  state.snapshotBusy = true;
  clearError();
  try {
    const res = await taxPlaygroundV2Api.importForm({
      sessionId: state.sessionId,
      userId: state.userId,
      snapshot,
      clearTurns: Boolean(clearTurns),
    });

    const form = (snapshot && snapshot.form) || {};
    if (Number(form.yearId) > 0) state.yearId = Number(form.yearId);
    if (Number(form.taxConfigVersionId) > 0) state.configVersionId = Number(form.taxConfigVersionId);
    if (Number(form.questionVersionId) > 0) state.questionVersionId = Number(form.questionVersionId);
    savePrefs();

    state.activeTurn = null;
    state.lastState = null;
    state.turnNotFound = false;
    if (clearTurns) {
      state.turns = [];
      state.turnsTotal = 0;
    }
    try {
      await loadTurns();
    } catch (e) {
      // swallowed on purpose; the import itself succeeded
    }
    try {
      await peekState();
    } catch (e) {
      // ditto — the imported form is already in the DB either way
    }
    return res;
  } catch (err) {
    setError(err);
    throw err;
  } finally {
    state.snapshotBusy = false;
  }
}

/**
 * (6) Open one turn fully inflated into the Inspector.
 * 🔴 A 404 (turn purged / another company's turn) is an EMPTY STATE (L15 /
 * TC-S3P4-62): it sets `turnNotFound` and resolves `null`. It must NOT set
 * `state.error` and must NOT re-throw, otherwise a dead deep link raises a red
 * snackbar. Every other failure behaves normally.
 * @param {number} turnId
 * @returns {Promise<?TurnDto>}
 */
async function openTurn(turnId) {
  state.loadingTurn = true;
  state.turnNotFound = false;
  clearError();
  try {
    const dto = await taxPlaygroundV2Api.getTurn(turnId);
    state.activeTurn = dto;
    state.lastState = null;
    return dto;
  } catch (err) {
    if (err && err.httpStatus === 404) {
      state.turnNotFound = true;
      state.activeTurn = null;
      return null;
    }
    setError(err);
    throw err;
  } finally {
    state.loadingTurn = false;
  }
}

/**
 * B7 deep link: apply `?session=&turn=` on mount. The store never imports the
 * router — writing the query back (and stripping a dead `?turn=`) is the view's job.
 * @param {{session?: string, turn?: (string|number)}} [query]
 * @returns {Promise<{turns: TurnSummary[], turn: ?TurnDto, notFound: boolean}>}
 */
async function applyDeepLink({ session, turn } = {}) {
  if (session != null && String(session) !== '') setSessionId(String(session));
  if (state.sessionId) {
    try {
      await loadTurns();
    } catch (e) {
      // a failed timeline must not stop us from opening the linked turn
    }
  }
  const n = Number(turn);
  if (Number.isFinite(n) && n > 0) {
    try {
      await openTurn(n);
    } catch (e) {
      // non-404 failures are already in state.error; a deep link never throws at the caller
    }
  }
  return { turns: state.turns, turn: state.activeTurn, notFound: state.turnNotFound };
}

// Preferences are read once, at module load, so the first render already shows
// the admin's last session/user/version selection.
loadPrefs();

export const taxPlaygroundV2Store = {
  state,
  clearError,
  setError,
  // actions
  loadOptions,
  send,
  peekState,
  reset,
  exportForm,
  importForm,
  loadTurns,
  openTurn,
  newSession,
  // getters / helpers
  isRepeatTurn,
  repeatStreak,
  isPgUserId,
  isTruncated,
  applyDeepLink,
  setSessionId,
  setUserId,
  setVersionSelection,
  setSettingsVersionId,
  setTimelineCollapsed,
  setInspectorFull,
  loadPrefs,
  savePrefs,
};

export default taxPlaygroundV2Store;
